import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { setLogoutHandler } from '../services/http-client';
import { User, LoginResponse } from '../interfaces/user.interface';
import { logger } from '../utils/logger';
import { Permission } from '../permissions/permission.enum';
import { RolePermissions } from '../permissions/role-permissions';
import { UserRole } from '../enums/user-role.enum';
import IdleWarningModal from '../components/IdleWarningModal';

interface AuthContextType {
  user: User | null;
  login: (loginResponse: LoginResponse) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  logout: () => {},
  loading: true,
  isAuthenticated: false,
  permissions: [],
  hasPermission: () => false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const path = window.location.pathname;
      const isPublicPath = path === '/login' || path === '/setup';

      try {
        // Check setup status unless already on a public auth page
        if (!isPublicPath) {
          const { isSetup } = await authService.getSetupStatus();
          if (!isSetup) {
            window.location.replace('/setup');
            return;
          }

          // Check for an existing session
          const user = await authService.getProfile({ skipAuthRedirect: true });
          setUser(user);
        }
      } catch {
        // No valid session — ProtectedRoute will redirect to /login
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (loginResponse: LoginResponse) => {
    // Expecting { access_token, ...user }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { access_token, ...userData } = loginResponse;
    // Token is stored in cookie by backend
    setUser(userData as unknown as User);
  };

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      logger.error('Logout failed:', error);
    }
    setUser(null);
    window.location.href = '/login';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register our full logout with the http-client so token expiry
  // goes through the same path as a manual logout (API call + state clear).
  useEffect(() => {
    setLogoutHandler(logout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const permissions =
    (user?.permissions as Permission[]) ||
    (user && user.role === UserRole.ADMIN
      ? RolePermissions[UserRole.ADMIN]
      : []);

  const hasPermission = (permission: Permission) => {
    return permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!user,
        permissions,
        hasPermission,
      }}
    >
      {children}
      {!!user && <IdleWarningModal />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
